import request from "../../../global_functions/request";

import {
  recursiveCamelCaseCipher,
  bugsnagPostgrestErrorHandler,
} from "../../../global_functions/postgrestApi/common";
import { useQueryClient } from "@tanstack/react-query";
import type {
  SourceAsyncReturnValues,
  FIELD_ID_VALUES,
  SourceId,
} from "../../../backpack-console/pages/Overview/types";
import { ledgerKeys } from "./queryKeyFactory";
import { VITE_LEDGER_BASE_URL } from "../../../env";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

const sources = {
  useQueryAll,
  mutations: {
    usePutSelectedSource,
  },
} as const;

export default sources;

// endpoint requires {feature: "Building Overview", access: "read"}
const getSources = async (
  siteId: string | number,
  fieldId: FIELD_ID_VALUES | undefined,
  {
    checkFeatureAccessHeader,
  }: { checkFeatureAccessHeader: CheckFeatureAccessHeader }
): Promise<Array<SourceAsyncReturnValues>> => {
  if (!fieldId) throw new Error("No field Id");
  return request
    .get(`${VITE_LEDGER_BASE_URL}/sites/${siteId}/fields/${fieldId}/sources`)
    .set(checkFeatureAccessHeader)
    .then(({ body }) => recursiveCamelCaseCipher(body))
    .catch((e) => bugsnagPostgrestErrorHandler(e));
};

function useQueryAll(siteId: number, fieldId: FIELD_ID_VALUES | undefined) {
  const queryKey = ledgerKeys.entity(siteId, fieldId, "sources");
  return useQueryWithAccessCheckLog(
    ["Building Overview", "read"],
    (checkFeatureAccessHeader) =>
      getSources(siteId, fieldId, { checkFeatureAccessHeader }),
    { queryKey, enabled: !!fieldId }
  );
}

// endpoint requires {feature: "Building Overview", access: "edit"}
const putSelectedSource = (
  siteId: string | number,
  sourceId: SourceId,
  fieldId: FIELD_ID_VALUES | undefined,
  {
    checkFeatureAccessHeader,
  }: { checkFeatureAccessHeader: CheckFeatureAccessHeader }
) => {
  if (!fieldId) throw new Error("No field Id!");
  return request
    .put(
      `${VITE_LEDGER_BASE_URL}/sites/${siteId}/fields/${fieldId}/sources/${sourceId}`
    )
    .set(checkFeatureAccessHeader);
};

function usePutSelectedSource(siteId: number, fieldId?: FIELD_ID_VALUES) {
  const queryKey = ledgerKeys.field(siteId, fieldId);
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit"],
    (sourceId: SourceId, checkFeatureAccessHeader) => {
      return putSelectedSource(siteId, sourceId, fieldId, {
        checkFeatureAccessHeader,
      });
    },
    {
      onSuccess: async () => await queryClient.invalidateQueries({ queryKey }),
    }
  );
}
